import React, { useState } from 'react';
import { useLoadedUser } from '../../../../hooks/api/useUser';
import { getIntl } from '../../../../utils/intl';
import addressToString from '../../../../utils/addressToString';
import UserInfoForm from './UserInfoForm';
import EnvironmentToggle from './EnvironmentToggle';
import { useAccountTabNavigation } from '../../../../utils/appNavigationNames';
import { useConfirmBackNavigationOnDirtyForm } from '../../FormExitValidation';
import Stack from '../../../v2BaseComponents/Stack';
import MainContentContainer from '../../../v2BaseComponents/MainContentContainer';
import { ActionButtonLayout } from '../../../v2BaseComponents/ActionButtonLayout';
import { getFallbackFromName, LoadableAvatar } from '../../../v2BaseComponents/Avatar';
import urls from '../../../../utils/urls';
import { APITypes } from '../../../../primary-record-types';
import LabelValueDisplay, { LabelValueItem } from '../../../v2BaseComponents/LabelValueDisplay';
import { AnimateLoadedContent } from '../../../v2BaseComponents/Loader';
import { useSignOutAndResetApp } from '../../../../hooks/useSignOutAndResetApp';
import { privacyPolicyUrl, termsOfServiceUrl } from '../../../../utils/constants';
import { useIsProApp } from '../../../../hooks/useAppType';
import { ClickableLinkText } from '../../../v2BaseComponents/Link';
import Text from '../../../v2BaseComponents/Text';
import useIsTestUser from '../../../../hooks/useIsTestUser';
import ToggleOrgPaidStatus from './ToggleOrgPaidStatus';
import FastenSandboxModeToggle from './FastenSandboxModeToggle';

export type UserAccountInformationScreenOwnProps = {
    defaultToEdit?: boolean;
};

export type AccountScreenInformationProps = UserAccountInformationScreenOwnProps & {};

const intl = getIntl('accountInformation', {
    email: 'Email',
    firstName: 'First Name',
    lastName: 'Last Name',
    dob: 'Date of Birth',
    preferredName: 'Preferred Name',
    phoneNumber: 'Phone Number',
    address: 'Address',
    edit: 'Edit',
    signOut: 'Sign Out',
    deleteAccount: 'Delete Account',
    termsOfService: 'Terms of Service',
    privacyPolicy: 'Privacy Policy',
    andSpacer: ' & ',
    tradeMarkDisclaimer: 'Primary Record® is a registered trademark of Primary Record Inc. All rights reserved.',
});

const getUserLabelValues = (user: APITypes.userService.MeResponse): LabelValueItem[] => [
    {
        label: intl('email'),
        value: user.email,
    },
    {
        label: intl('firstName'),
        value: user.firstName,
    },
    {
        label: intl('lastName'),
        value: user.lastName,
    },

    {
        label: intl('preferredName'),
        value: user.preferredName,
    },
    {
        label: intl('dob'),
        value: user.dob,
    },
    {
        label: intl('phoneNumber'),
        value: user.phoneNumber,
        linkType: 'phone',
    },
    {
        label: intl('address'),
        value: addressToString({
            address1: user.streetAddress1,
            address2: user.streetAddress2,
            city: user.city,
            state: user.state,
            zipCode: user.zipCode,
        }),
        linkType: 'address',
    },
];

const UserAccountInformationScreen: React.FC<AccountScreenInformationProps> = (props) => {
    const accountTabNavigation = useAccountTabNavigation();
    useConfirmBackNavigationOnDirtyForm(accountTabNavigation);
    const [isEditing, setIsEditing] = useState(!!props.defaultToEdit);
    const navigator = useAccountTabNavigation();
    const { signOutAndResetApp, isResetting } = useSignOutAndResetApp();
    const { user, updateUser, isUpdatingUser } = useLoadedUser({
        onUpdateSuccess: () => setIsEditing(false),
    });
    const isProApp = useIsProApp();
    const isTestUser = useIsTestUser();

    if (isEditing) {
        return (
            <AnimateLoadedContent isLoading={isUpdatingUser}>
                {() => <UserInfoForm user={user} onUpdate={updateUser} />}
            </AnimateLoadedContent>
        );
    } else {
        return (
            <ActionButtonLayout
                whiteBackground
                scrollable
                actions={[
                    {
                        icon: 'Edit2',
                        title: intl('edit'),
                        onPress: () => {
                            setIsEditing(true);
                        },
                        testID: 'edit-account-information-button',
                    },
                    {
                        icon: 'LogOut',
                        title: intl('signOut'),
                        isDisabled: isResetting,
                        onPress: signOutAndResetApp,
                        testID: 'action-signOut',
                        show: isProApp,
                    },
                    {
                        icon: 'Trash2',
                        title: intl('deleteAccount'),
                        onPress: () => navigator.navigate('account-deletion-confirmation'),
                        displayInMoreMenu: true,
                        testID: 'navigate-to-delete-account-button',
                    },
                ]}
            >
                <MainContentContainer style={{ minHeight: '100%' }}>
                    <Stack vertical fullWidth flex={1}>
                        <Stack horizontal fullWidth justify="center">
                            <LoadableAvatar
                                avatarUrl={urls.api.users.avatar}
                                size="xxl"
                                fallbackName={getFallbackFromName(user.preferredName || user.firstName, user.lastName)}
                            />
                        </Stack>
                        <LabelValueDisplay items={getUserLabelValues(user)} />
                        <EnvironmentToggle />
                        {isTestUser ? <ToggleOrgPaidStatus /> : null}
                        {isTestUser ? <FastenSandboxModeToggle /> : null}
                    </Stack>
                    {isProApp ? (
                        <Stack vertical fullWidth style={{ marginTop: 16 }} space={2}>
                            <Text variant="body-1">
                                <ClickableLinkText linkType="url" variant="body-1" linkLocation={privacyPolicyUrl}>
                                    {intl('privacyPolicy')}
                                </ClickableLinkText>
                                <Text variant="body-1">{intl('andSpacer')}</Text>
                                <ClickableLinkText linkType="url" variant="body-1" linkLocation={termsOfServiceUrl}>
                                    {intl('termsOfService')}
                                </ClickableLinkText>
                            </Text>
                            <Text variant="body-1">{intl('tradeMarkDisclaimer')}</Text>
                        </Stack>
                    ) : null}
                </MainContentContainer>
            </ActionButtonLayout>
        );
    }
};

export default React.memo(UserAccountInformationScreen);
